// Final Approval Letter (FAL) computation engine
// Robust, deterministic, and extensible computation with full control for additions/removals/overrides.
// This module is PURE (no side-effects). Integrators should persist results to Firestore separately.

/*
Contract
- Inputs:
  - application: full application object (reads mv.dischargeReview.items, pv policy verification)
  - options: {
      policy: workflow.policyVerification or explicit policy payload,
      dischargeItems: array of items to compute on (falls back to mv.dischargeReview.items),
      overrides: {
        copay: { enabled, amountOverride, percentOverride, reason, by, at },
        sublimits: [ { key, amountOverride, reason, by, at } ]
      },
      manualAdjustments: [ { id, label, amount, reason, addedBy, addedAt } ],
      rounding: 'nearest'|'down'|'up',
    }
- Output:
  {
    items: [ { ...input, admissible, nonAdmissible, proportionateDeduction, sublimitDeduction, deductibleApplied, payableBeforeCopay, copayDeduction, finalPayable, notes: [] } ],
    totals: {
      billed,
      eligible,
      nonAdmissibles,
      proportionate,
      sublimits,
      deductible,
      payableBeforeCopay,
      copay,
      manualAdjustments,
      finalSettlement,
    },
    breakdown: [ { key, label, amount, meta } ], // deterministic ordering
    overridesMeta: { copay?: {...}, sublimits?: [...] },
    version: 1,
  }
*/

const asNumber = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const round = (v, mode = 'nearest') => {
  switch (mode) {
    case 'down': return Math.floor(v);
    case 'up': return Math.ceil(v);
    default: return Math.round(v);
  }
};

const safeArray = (arr) => Array.isArray(arr) ? arr : [];

// Room/ICU proportionate calculation based on policy room eligibility vs occupied room cost
function computeProportionateDeduction(item, policy, context) {
  // Only apply on room/bed/icu categories typically; allow extensibility via category matchers
  const cat = (item.category || '').toLowerCase();
  const isRoom = ['room', 'bed', 'icu', 'hdu', 'nicu', 'picu'].some(k => cat.includes(k));
  if (!isRoom) return 0;

  // CRITICAL FIX: If doctor has already set eligibleUnitRate or eligibleAmount, 
  // it means they've already applied room/ICU deductions manually.
  // Skip automatic proportionate deduction to avoid DOUBLE DEDUCTION.
  if (item.eligibleUnitRate !== undefined && item.eligibleUnitRate !== null && item.eligibleUnitRate !== '') {
    return 0; // Doctor already set eligible rate, don't recalculate
  }
  
  // Also skip if eligibleAmount is explicitly set and differs from billed
  const qty = asNumber(item.quantity) || 1;
  const billedAmt = asNumber(item.billedAmount) || (asNumber(item.unitRate) * qty);
  const eligibleAmt = asNumber(item.eligibleAmount);
  if (eligibleAmt >= 0 && eligibleAmt < billedAmt) {
    // Doctor has already applied a deduction by setting lower eligible amount
    return 0; // Deduction already reflected in eligible amount
  }

  const roomPolicy = policy?.roomEligibility || {};
  const capPerDay = asNumber(roomPolicy.roomAmountPerDay) || 0; // 0 indicates no numeric cap; if 'room_type' capping was used without numeric mapping, skip
  const icuCapPerDay = asNumber(roomPolicy.icuAmountPerDay) || 0;

  const isICU = ['icu', 'hdu', 'nicu', 'picu'].some(k => cat.includes(k));
  const cap = isICU ? icuCapPerDay : capPerDay;
  if (!(cap > 0)) return 0;

  const eligibleUnit = Math.min(asNumber(item.unitRate) || asNumber(item.eligibleUnitRate) || 0, cap);
  const billedUnit = asNumber(item.unitRate) || asNumber(item.billedAmount) / (qty || 1) || 0;
  const proportionate = Math.max(0, (billedUnit - eligibleUnit) * qty);
  return proportionate;
}

// Enhanced sublimit deduction with selection capability (like copay selection)
function computeSublimitDeduction(items, policy, overrides) {
  const sublimits = safeArray(policy?.deductiblesAndSublimits?.ailmentProcedureSublimits);
  if (!sublimits.length) return { map: {}, total: 0, meta: [], availableLimits: [] };

  const resultMap = {};
  const meta = [];
  const availableLimits = [];
  let total = 0;

  // Get sublimit selection from overrides (similar to copay selection)
  const sublimitSelection = overrides?.sublimitSelection || {};

  // Strategy: for each sublimit entry, identify matching items and allow selection
  sublimits.forEach((sl, idx) => {
    const key = `sublimit_${idx}`;
    const limit = asNumber(sl.sublimitAmount);
    const ail = (sl.ailmentProcedure || '').toLowerCase();
    if (!(limit > 0) || !ail) return;

    // Add to available limits list for UI selection
    availableLimits.push({
      key,
      ailmentProcedure: sl.ailmentProcedure,
      sublimitAmount: limit,
      description: `Sublimit for ${sl.ailmentProcedure}: ₹${limit.toLocaleString('en-IN')}`
    });

    // Check if this sublimit is selected (default: none selected unless explicitly chosen)
    const isSelected = sublimitSelection[key] === true;
    if (!isSelected) return;

    const matched = items.filter(it =>
      String(it.itemName || '').toLowerCase().includes(ail) ||
      String(it.category || '').toLowerCase().includes(ail) ||
      String(it.remarks || '').toLowerCase().includes(ail)
    );

    const admissibleSum = matched.reduce((s, it) => s + Math.max(0, asNumber(it.eligibleAmount)), 0);

    // NEW LOGIC: When sublimit is selected, the approved amount for this category 
    // should not exceed the sublimit even if bill is higher
    // So deduction = max(0, admissibleSum - limit) but we cap the approved amount at limit
    let deduction = Math.max(0, admissibleSum - limit);

    // Apply override if present
    const ov = safeArray(overrides?.sublimits).find(o => o.key === key);
    if (ov && asNumber(ov.amountOverride) >= 0) {
      deduction = asNumber(ov.amountOverride);
      meta.push({ key, reason: ov.reason || 'override', by: ov.by, at: ov.at, original: Math.max(0, admissibleSum - limit) });
    }

    // Add this sublimit to the total (this is the capped amount, not deduction)
    const approvedAmount = Math.min(admissibleSum, limit);
    resultMap[key] = { 
      label: `Sublimit: ${sl.ailmentProcedure}`, 
      amount: deduction,
      approvedAmount,
      totalBilled: admissibleSum,
      sublimitCap: limit,
      matchedItems: matched.length
    };
    total += deduction;
  });

  return { map: resultMap, total, meta, availableLimits };
}

// Non-admissible logic: when item.payableType is non_payable or deduction, treat as non-admissible.
function computeNonAdmissible(item) {
  const t = (item.payableType || '').toLowerCase();
  if (t === 'non_payable') return Math.max(0, asNumber(item.billedAmount) || 0);
  if (t === 'deduction') return Math.max(0, (asNumber(item.billedAmount) || 0) - (asNumber(item.eligibleAmount) || 0));
  return 0;
}

// Compulsory deductible from policy (flat)
function computeCompulsoryDeductible(policy) {
  return Math.max(0, asNumber(policy?.deductiblesAndSublimits?.compulsoryDeductible));
}

// Co-pay application post-deductible sublimits etc.
function computeCopay(amountAfterPreDeductions, policy, overrides, context) {
  const cp = policy?.coPayStructure || {};
  let copayAmount = 0;

  const getCopayBreakdown = () => {
    const breakdown = [];
    
    // Build list of all applicable copays with their details
    if (cp.generalCoPayApplicable) {
      breakdown.push({
        type: 'general',
        label: 'General Co-pay',
        percent: asNumber(cp.generalCoPayValue),
        applicable: true
      });
    }
    
    if (cp.nonNetworkCoPayApplicable) {
      breakdown.push({
        type: 'nonNetwork',
        label: 'Non-Network Hospital Co-pay',
        percent: asNumber(cp.nonNetworkCoPayValue),
        applicable: true
      });
    }
    
    if (cp.locationBasedCoPayApplicable) {
      // Show both metro and non-metro if configured
      if (asNumber(cp.metroCoPayValue) > 0) {
        breakdown.push({
          type: 'locationMetro',
          label: 'Metro Location Co-pay',
          percent: asNumber(cp.metroCoPayValue),
          applicable: true
        });
      }
      if (asNumber(cp.nonMetroCoPayValue) > 0) {
        breakdown.push({
          type: 'locationNonMetro',
          label: 'Non-Metro Location Co-pay',
          percent: asNumber(cp.nonMetroCoPayValue),
          applicable: true
        });
      }
    }
    
    if (cp.parentalCoPayApplicable) {
      breakdown.push({
        type: 'parental',
        label: `Parental Co-pay (Age ${cp.parentalCoPayAgeThreshold || '60'}+)`,
        percent: asNumber(cp.parentalCoPayValue),
        applicable: true
      });
    }
    
    if (cp.mandatoryCoPayApplicable) {
      breakdown.push({
        type: 'mandatory',
        label: 'Mandatory Co-pay',
        percent: asNumber(cp.mandatoryCoPayValue),
        applicable: true
      });
    }
    
    if (cp.voluntaryCoPayApplicable) {
      breakdown.push({
        type: 'voluntary',
        label: 'Voluntary Co-pay',
        percent: asNumber(cp.voluntaryCoPayValue),
        applicable: true
      });
    }
    
    if (cp.nonIntimationCoPayApplicable && policy?.intimationDetails?.intimationMandatory && !policy?.intimationDetails?.intimationDone) {
      breakdown.push({
        type: 'nonIntimation',
        label: 'Non-Intimation Co-pay',
        percent: asNumber(cp.nonIntimationCoPayValue),
        applicable: true
      });
    }
    
    return breakdown.filter(item => item.percent > 0);
  };

  const copayBreakdown = getCopayBreakdown();
  
  // Respect copay selection if provided in overrides
  const copaySelection = overrides?.copaySelection || {};
  
  // Calculate active copays based on selection (default: all applicable copays are included)
  const activeCopays = copayBreakdown.filter(item => {
    // If copaySelection is provided, respect it; otherwise include all applicable copays
    return copaySelection.hasOwnProperty(item.type) ? copaySelection[item.type] : true;
  });

  // Sum percentages of all active/applicable copays (as per requirement)
  const sumPercent = activeCopays.reduce((acc, item) => acc + (asNumber(item.percent) || 0), 0);
  let percent = sumPercent;

  // Allow global overrides (amount or percent override)
  const cpOverride = overrides?.copay;
  let meta = { breakdown: copayBreakdown, activeCopays, sumFromPolicy: sumPercent, percentFromPolicy: sumPercent };
  
  if (cpOverride && (asNumber(cpOverride.amountOverride) >= 0 || asNumber(cpOverride.percentOverride) >= 0)) {
    const original = round(amountAfterPreDeductions * (percent / 100));
    if (asNumber(cpOverride.amountOverride) >= 0) {
      copayAmount = asNumber(cpOverride.amountOverride);
    } else if (asNumber(cpOverride.percentOverride) >= 0) {
      percent = asNumber(cpOverride.percentOverride);
      copayAmount = round(amountAfterPreDeductions * (percent / 100));
    }
    meta = { 
      ...meta, 
      reason: cpOverride.reason || 'override', 
      by: cpOverride.by, 
      at: cpOverride.at, 
      original 
    };
  } else {
    copayAmount = round(amountAfterPreDeductions * (percent / 100));
  }

  return { copayAmount: Math.max(0, copayAmount), percent, meta, breakdown: copayBreakdown };
}

export function computeFalSettlement(application, options = {}) {
  const rounding = options.rounding || 'nearest';
  const policy = options.policy || application?.workflow?.policyVerification || {};
  const mv = application?.workflow?.medicalVerification || {};
  const rawItems = safeArray(options.dischargeItems || mv?.dischargeReview?.items);
  const items = rawItems.map((it) => ({ ...it }));

  // Pre-compute line components
  let totalBilled = 0;
  let totalEligible = 0;
  let totalNonAdmissible = 0;
  let totalProportionate = 0;
  const lineResults = [];

  // First pass: non-admissibles and proportionate (room/icu)
  for (const it of items) {
    const billed = asNumber(it.billedAmount) || (asNumber(it.unitRate) * (asNumber(it.quantity) || 1));
    const eligible = asNumber(it.eligibleAmount);
    const nonAdmiss = computeNonAdmissible(it);
    const prop = computeProportionateDeduction(it, policy, { rounding });

    totalBilled += billed;
    totalEligible += Math.max(0, eligible);
    totalNonAdmissible += nonAdmiss;
    totalProportionate += prop;

    lineResults.push({
      ...it,
      billed,
      admissible: Math.max(0, eligible),
      nonAdmissible: nonAdmiss,
      proportionateDeduction: prop,
      notes: [],
    });
  }

  // Enhanced logic for copay + sublimit combination
  // Step 1: Apply sublimits and get available sublimit info
  const sublimitRes = computeSublimitDeduction(lineResults, policy, options.overrides);
  
  // Step 2: Calculate base amount after non-admissibles and proportionate
  const baseAmount = Math.max(0, totalEligible - totalNonAdmissible - totalProportionate);
  
  // Step 3: Apply compulsory deductible
  const compDeductible = computeCompulsoryDeductible(policy);
  const afterDeductible = Math.max(0, baseAmount - compDeductible);
  
  // Step 4: Apply copay first (on the amount after deductible)
  const { copayAmount, percent: copayPercent, meta: copayMeta } = computeCopay(afterDeductible, policy, options.overrides, { rounding });
  const afterCopay = Math.max(0, afterDeductible - copayAmount);
  
  // Step 5: Handle sublimit logic
  let finalAfterSublimits = afterCopay;
  let sublimitTotal = 0;
  
  // Check if any sublimits are selected
  const selectedSublimits = Object.keys(options.overrides?.sublimitSelection || {}).filter(
    key => options.overrides.sublimitSelection[key] === true
  );
  
  if (selectedSublimits.length > 0) {
    // Calculate total selected sublimit amount
    const totalSelectedSublimits = selectedSublimits.reduce((sum, key) => {
      const sublimit = sublimitRes.availableLimits.find(sl => sl.key === key);
      return sum + (sublimit ? sublimit.sublimitAmount : 0);
    }, 0);
    
    // Take the lower of: remaining amount after copay OR total selected sublimits
    finalAfterSublimits = Math.min(afterCopay, totalSelectedSublimits);
    sublimitTotal = Math.max(0, afterCopay - finalAfterSublimits);
  } else {
    // No sublimits selected, use traditional sublimit deduction logic
    sublimitTotal = sublimitRes.total;
    finalAfterSublimits = Math.max(0, afterCopay - sublimitTotal);
  }

  // Step 6: Manual adjustments
  const adjustmentsTotal = safeArray(options.manualAdjustments).reduce((s, a) => {
    const amt = Math.round(asNumber(a.amount));
    const mode = (a.mode || '').toLowerCase();
    // If mode is 'deduct', treat as negative; default and 'add' are positive
    const signed = mode === 'deduct' ? -Math.abs(amt) : Math.abs(amt);
    return s + signed;
  }, 0);

  const finalSettlement = Math.max(0, round(finalAfterSublimits + adjustmentsTotal, rounding));
  
  // For compatibility, set payableBeforeCopay
  const payableBeforeCopay = afterDeductible;

  // Enhanced breakdown with detailed payment flow
  const breakdown = [
    { key: 'billed', label: 'Total Billed', amount: round(totalBilled, rounding) },
    { key: 'eligible', label: 'Total Eligible (Admissible base)', amount: round(totalEligible, rounding) },
    { key: 'non_admissibles', label: 'Non-Admissibles', amount: round(totalNonAdmissible, rounding) },
    { key: 'proportionate', label: 'Room/ICU Proportionate Deduction', amount: round(totalProportionate, rounding) },
    { key: 'after_basic_deductions', label: 'After Basic Deductions', amount: round(baseAmount, rounding) },
    { key: 'deductible', label: 'Compulsory Deductible', amount: round(compDeductible, rounding) },
    { key: 'after_deductible', label: 'After Deductible', amount: round(afterDeductible, rounding) },
    { key: 'copay', label: `Co-pay (${copayPercent}%)`, amount: round(copayAmount, rounding), meta: copayMeta || undefined },
    { key: 'after_copay', label: 'After Co-pay', amount: round(afterCopay, rounding) },
    { key: 'sublimits', label: 'Sublimit Application', amount: round(sublimitTotal, rounding), meta: { ...sublimitRes.meta, selectedLimits: selectedSublimits } },
    { key: 'after_sublimits', label: 'After Sublimits', amount: round(finalAfterSublimits, rounding) },
    { key: 'manual_adjustments', label: 'Manual Adjustments', amount: round(adjustmentsTotal, rounding), meta: safeArray(options.manualAdjustments) },
    { key: 'final_settlement', label: 'Final Settlement Amount', amount: round(finalSettlement, rounding) },
  ];

  // Enhanced FAL summary for payment breakdown
  const falSummary = {
    approvedAmount: round(finalSettlement, rounding),
    patientCoPayAmount: round(copayAmount, rounding),
    patientSublimitAmount: round(sublimitTotal, rounding),
    patientDeductibleAmount: round(compDeductible, rounding),
    totalPatientPayment: round(copayAmount + sublimitTotal + compDeductible, rounding),
    hospitalPaymentFromInsurer: round(finalSettlement, rounding),
    remainingPatientPayment: round(Math.max(0, totalBilled - finalSettlement), rounding)
  };

  // Enrich line items with derived amounts (distribute proportionate/sublimit proportionally if needed later)
  const lineWithFinals = lineResults.map((r) => {
    // For now, compute finalPayable per line as eligible (minus non-admissible portion and an equal share of prop/sublimit if category matches)
    // Deterministic and conservative approach: non-admissible fully removes that line; proportionate applied only on room-like categories; sublimit not distributed per line unless matched — we already matched by ailment text, but we keep total at header level.
    const base = Math.max(0, r.admissible - r.nonAdmissible - (['room', 'bed', 'icu', 'hdu', 'nicu', 'picu'].some(k => String(r.category || '').toLowerCase().includes(k)) ? r.proportionateDeduction : 0));
    return {
      ...r,
      payableBeforeCopay: round(Math.max(0, base), rounding),
      copayDeduction: 0, // not allocated per line in this version
      finalPayable: round(Math.max(0, base), rounding),
    };
  });

  return {
    items: lineWithFinals,
    totals: {
      billed: round(totalBilled, rounding),
      eligible: round(totalEligible, rounding),
      nonAdmissibles: round(totalNonAdmissible, rounding),
      proportionate: round(totalProportionate, rounding),
      sublimits: round(sublimitTotal, rounding),
      deductible: round(compDeductible, rounding),
      payableBeforeCopay: round(payableBeforeCopay, rounding),
      copay: round(copayAmount, rounding),
      manualAdjustments: round(adjustmentsTotal, rounding),
      finalSettlement: round(finalSettlement, rounding),
    },
    breakdown,
    falSummary,
    sublimitInfo: {
      availableLimits: sublimitRes.availableLimits,
      selectedLimits: selectedSublimits,
      totalSelectedAmount: selectedSublimits.reduce((sum, key) => {
        const sublimit = sublimitRes.availableLimits.find(sl => sl.key === key);
        return sum + (sublimit ? sublimit.sublimitAmount : 0);
      }, 0)
    },
    overridesMeta: {
      copay: copayMeta ? { ...copayMeta, percent: copayPercent } : undefined,
      sublimits: sublimitRes.meta,
    },
    version: 2, // Updated version for new logic
  };
}

export default {
  computeFalSettlement,
};
